import React, { FC, useCallback, useEffect, useState } from "react";
import Select from "react-select";
import {
  customSelectInputStyles,
  customSelectInputStylesError,
} from "../../components/Form/customSelectStyles";
import Icon from "../../components/Icon/Icon";
import { Span } from "../../components/Text/Text";
import Tooltip from "../../components/Tooltip/Tooltip";
import {
  IconColorE,
  LangE,
  TooltipPlaceE,
  UserRoleE,
} from "../../models/enums";
import { VersionT } from "../../models/models";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  selectCurrentLang,
  selectDirection,
} from "../../store/selectors/appSelectors";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { selectDeliveryWizardData } from "../../store/selectors/deliveryWizardSelector";
import { removeVersionByAdId } from "../../store/slices/deliveryWizardSlice";
import {
  DEFAULT_VERSION_NAME,
  UNCLEAR_HOUSE_NUMBER_VALUE,
} from "../../utils/constants";
import { convertNumToMinSec } from "../../utils/helpers/convertNumToMinSec";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import {
  fileTypeHebrewSO,
  fileTypeSO,
  selectValue,
} from "../../utils/helpers/selectOptions";
import { clss } from "../../utils/helpers/styleHelpers";
import { useTranslate } from "../../utils/hooks/useTranslate";
import BroadcasterHouseNumbers from "./components/BroadcasterHouseNumbers/BroadcasterHouseNumbers";
import { useVersionState } from "./helpers/useVersionState";

import styles from "./styles.module.scss";

type DeliveryVersionRowPropTypes = {
  defaultData: VersionT;
  index: number;
  isShortEdit: boolean;
};

const DeliveryVersionRow: FC<DeliveryVersionRowPropTypes> = (props) => {
  const { defaultData, index, isShortEdit } = props;
  const {
    editableVersionIndex,
    delivery: { Versions = [] },
  } = useAppSelector(selectDeliveryWizardData);
  const user = useAppSelector(selectCurrentUser);
  const lang = useAppSelector(selectCurrentLang);
  const direction = useAppSelector(selectDirection);
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const {
    name,
    setName,
    requiredLength,
    setRequiredLength,
    selectedOption,
    setSelectedOption,
    selectedSecondOption,
    setSelectedSecondOption,
    isChannel5or9,
    setIsChannel5or9,
    useFor2Spots,
    setUseFor2Spots,
    selectError,
    setSelectError,
    nameError,
    setNameError,
    requiredLengthError,
    setRequiredLengthError,
    denyChanges,
    saveChanges,
    startEdit,
    houseNumber,
    setHouseNumber,
    sponsorshipHouseNumber,
    setSponsorshipHouseNumber,
  } = useVersionState(defaultData);

  const handleAddEmptyVersion = useCallback(() => {
    editableVersionIndex === index &&
      saveChanges(editableVersionIndex === index, false);
  }, [
    name,
    selectedOption?.value,
    requiredLength,
    isChannel5or9,
    useFor2Spots,
    editableVersionIndex,
    index,
  ]);

  useEffect(() => {
    document.addEventListener(
      "addEmptyVersionAfterSaving",
      handleAddEmptyVersion
    );
    return () => {
      document.removeEventListener(
        "addEmptyVersionAfterSaving",
        handleAddEmptyVersion
      );
    };
  }, [handleAddEmptyVersion]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && index === editableVersionIndex) {
      saveChanges(false, false);
    }
  };

  useEffect(() => {
    if (editableVersionIndex === index) {
      setName(
        defaultData.Name === DEFAULT_VERSION_NAME ? "" : defaultData.Name
      );
      setRequiredLength(defaultData.RequiredLength);
      setSelectedOption(
        selectValue(
          lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
          +defaultData.FileType
        )
      );
      setIsChannel5or9(defaultData.isChannel5or9);
      setUseFor2Spots(defaultData.useFor2Spots || useFor2Spots);
    }
  }, [editableVersionIndex]);

  const remove = (versionIndex) => {
    dispatch(removeVersionByAdId(Versions[versionIndex].AdId));
  };

  const ViewVersionData = () =>
    isMobileOrTablet() ? (
      <div className={styles.versionRowWrapperMobile}>
        <div className={styles.versionRowTitleMobile}>
          <Icon name="clockFilled" color={IconColorE.GRAY} />
          <span>{Versions[index].Name}</span>
        </div>
        <div className={styles.editDeleteIcons}>
          {editableVersionIndex === -1 && (
            <Icon
              name="edit"
              onClick={() => startEdit(index)}
              title="Edit"
              className={styles.editIcon}
              color={IconColorE.DARK_GRAY}
            />
          )}
          {editableVersionIndex === -1 &&
            Versions.length > 1 &&
            !isShortEdit && (
              <Icon
                name="bin"
                onClick={() => remove(index)}
                title="Remove"
                className={styles.editIcon}
                color={IconColorE.DARK_GRAY}
              />
            )}
        </div>
        <div className={styles.checkedData}>
          <Span>{Versions[index].isChannel5or9 ? "MXF format" : ""}</Span>
          <Span>
            {Versions[index].useFor2Spots ? "As an ad and sponsorships" : ""}
          </Span>
        </div>
        <span className={styles.requiredLengthMobile}>
          {convertNumToMinSec(Versions[index].RequiredLength)}
        </span>
        <span>{selectedOption?.label}</span>
      </div>
    ) : (
      <div className={styles.versionRowWrapper}>
        <div className={styles.versionRow}>
          <span
            title={
              Versions[index].Name?.length > 30 ? Versions[index].Name : ""
            }
          >
            {Versions[index].Name}
          </span>
          <span>{selectedOption?.label}</span>
          <span>{convertNumToMinSec(Versions[index].RequiredLength)}</span>
          <div className={styles.checkedData}>
            <Span>
              {Versions[index].isChannel5or9
                ? "Channel 5 or Channel 9 (MXF format)"
                : ""}
            </Span>
            <Span>
              {Versions[index].useFor2Spots
                ? "Use this version as an ad and sponsorships as well"
                : ""}
            </Span>
          </div>
          <div className={styles.editDeleteIcons}>
            {editableVersionIndex === -1 && (
              <Icon
                name="edit"
                onClick={() => startEdit(index)}
                title="Edit"
                className={styles.editIcon}
                color={IconColorE.DARK_GRAY}
              />
            )}
            {editableVersionIndex === -1 && Versions.length > 1 && (
              <Icon
                name="bin"
                onClick={() => remove(index)}
                title="Remove"
                className={styles.editIcon}
                color={IconColorE.DARK_GRAY}
              />
            )}
          </div>
        </div>
        <BroadcasterHouseNumbers
          houseNumber={
            houseNumber === UNCLEAR_HOUSE_NUMBER_VALUE ? "" : houseNumber
          }
          sponsorshipHouseNumber={
            sponsorshipHouseNumber === UNCLEAR_HOUSE_NUMBER_VALUE
              ? ""
              : sponsorshipHouseNumber
          }
          setHouseNumber={setHouseNumber}
          setSponsorshipHouseNumber={setSponsorshipHouseNumber}
          useFor2Spots={useFor2Spots}
          SecondHouseNumberType={selectedSecondOption?.value}
          setSecondHouseNumberType={setSelectedSecondOption}
        />
      </div>
    );

  return editableVersionIndex === index ? (
    <div
      className={styles.versionRowWrapper}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{ direction }}
    >
      <div className={styles.versionRow}>
        <input
          type="text"
          value={name}
          onChange={(e) => {
            const value = e.target.value.replace(/\,|\;/g, "");
            setName(value?.trim().length ? value : value.trim());
            setNameError(false);
          }}
          className={clss([styles.field, nameError ? styles.errorField : ""])}
          maxLength={45}
          placeholder={t`Version name`}
        />
        {name.length !== 0 &&
        name.length <= 45 &&
        !nameError &&
        !isShortEdit ? (
          <p className={styles.maxNameLengthHint}>
            <Span>Max allowed chars</Span> {name?.length || 0}/45
          </p>
        ) : null}
        <div className={styles.selectWrapper}>
          {/*@ts-ignore*/}
          <Select
            options={lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO}
            defaultValue={selectedOption}
            onChange={(option) => {
              setSelectedOption(option);
              setSelectError(false);
            }}
            isSearchable={false}
            styles={
              selectError
                ? customSelectInputStylesError(lang)
                : customSelectInputStyles(lang)
            }
            placeholder={t`Content type`}
            menuPortalTarget={document.body}
          />
        </div>
        <input
          type="number"
          placeholder={isMobileOrTablet() ? "Required length (sec)" : ""}
          min={1}
          step={1}
          pattern="\d*" // only numbers
          value={requiredLength}
          onChange={(e) => {
            const value = parseInt(String(e.target.value));

            setRequiredLength(value);
            setRequiredLengthError(false);
          }}
          className={clss([
            styles.field,
            requiredLengthError ? styles.errorField : "",
          ])}
          disabled={isShortEdit}
        />
        {!isShortEdit ? (
          <div className={styles.versionCheckboxes}>
            <div className={styles.checkboxWrapper}>
              <input
                type="checkbox"
                checked={isChannel5or9}
                onChange={(e) => setIsChannel5or9(e.target.checked)}
                className={styles.checkbox}
                disabled={isShortEdit}
              />
              <span className={styles.mark} />
              <Span>Channel 5 or Channel 9 (MXF format)</Span>
              <Tooltip
                text="Mark this version if it is for Channel 5 or Channel 9 (MXF format)"
                delayShow={300}
                place={TooltipPlaceE.LEFT}
                isShowTooltip
                trigger="click"
              >
                <div
                  className={styles.infoTooltipWrapper}
                  onClickCapture={(e) => e.stopPropagation()}
                >
                  <Icon name="info" />
                </div>
              </Tooltip>
            </div>
            {(user?.role === UserRoleE.Broadcaster ||
              +requiredLength === 6) && (
              <div className={styles.checkboxWrapper}>
                <input
                  type="checkbox"
                  checked={useFor2Spots}
                  onChange={(e) => setUseFor2Spots(e.target.checked)}
                  className={styles.checkbox}
                  disabled={isShortEdit}
                />
                <span className={styles.mark} />
                <Span>Use this version as an ad and sponsorships as well</Span>
                <Tooltip
                  text="Is this version will be used as an advertisement and sponsorships as well?"
                  delayShow={300}
                  place={TooltipPlaceE.LEFT}
                  isShowTooltip
                  trigger="click"
                >
                  <div
                    className={styles.infoTooltipWrapper}
                    onClickCapture={(e) => e.stopPropagation()}
                  >
                    <Icon name="info" />
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        ) : (
          <div />
        )}
        <div className={styles.versionControlButtons}>
          <Span
            onClick={() => saveChanges(false, false)}
            id="save-house-numbers-button"
          >
            Save
          </Span>
          <Icon
            name="close"
            onClick={() => denyChanges(Versions.length, index)}
            title="Cancel"
            color={IconColorE.GRAY}
          />
        </div>
      </div>
      <div className={styles.errorsWrapper}>
        <span>{nameError ? t`Name is required` : ""}</span>
        <span>{selectError ? t`Content type is required` : ""}</span>
        <span>{requiredLengthError ? t`1 sec min` : ""}</span>
      </div>
      {!isShortEdit && (
        <BroadcasterHouseNumbers
          houseNumber={
            houseNumber === UNCLEAR_HOUSE_NUMBER_VALUE ? "" : houseNumber
          }
          sponsorshipHouseNumber={
            sponsorshipHouseNumber === UNCLEAR_HOUSE_NUMBER_VALUE
              ? ""
              : sponsorshipHouseNumber
          }
          setHouseNumber={setHouseNumber}
          setSponsorshipHouseNumber={setSponsorshipHouseNumber}
          useFor2Spots={useFor2Spots}
          SecondHouseNumberType={selectedSecondOption?.value}
          setSecondHouseNumberType={setSelectedSecondOption}
          isEditMode
        />
      )}
    </div>
  ) : (
    <ViewVersionData />
  );
};

export default DeliveryVersionRow;
